import { useEffect, useMemo, useState } from "react";
import { publicationCounts, publications } from "../data";
import { PageIntro } from "../components/Shell";
import { PublicationRow } from "../components/Records";
import type { PublicationType } from "../types";

type TypeFilter = "all" | PublicationType;
type SortOrder = "newest" | "oldest";

function initialParam(name: string) {
  return new URLSearchParams(window.location.search).get(name) || "";
}

export default function PublicationsPage() {
  const [query, setQuery] = useState(() => initialParam("q"));
  const [type, setType] = useState<TypeFilter>(() => {
    const value = initialParam("type");
    return ["international", "domestic", "book"].includes(value)
      ? (value as PublicationType)
      : "all";
  });
  const [sort, setSort] = useState<SortOrder>(() =>
    initialParam("sort") === "oldest" ? "oldest" : "newest"
  );

  useEffect(() => {
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (type !== "all") params.set("type", type);
    if (sort !== "newest") params.set("sort", sort);
    const next = `${window.location.pathname}${params.size ? `?${params}` : ""}`;
    window.history.replaceState({}, "", next);
  }, [query, type, sort]);

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    return publications
      .filter((publication) => type === "all" || publication.type === type)
      .filter((publication) => {
        if (!normalizedQuery) return true;
        return [publication.title, publication.authors, publication.venue, publication.year]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase()
          .includes(normalizedQuery);
      })
      .sort((a, b) => (sort === "newest" ? b.year - a.year : a.year - b.year));
  }, [query, sort, type]);

  const groups = useMemo(() => {
    const byYear = new Map<number, typeof filtered>();
    filtered.forEach((publication) => {
      const group = byYear.get(publication.year) || [];
      group.push(publication);
      byYear.set(publication.year, group);
    });
    return [...byYear.entries()];
  }, [filtered]);

  return (
    <>
      <PageIntro
        label="Publications"
        title="출판 기록"
        aside={
          <>
            총 {publicationCounts.all}건 · 국제 {publicationCounts.international} · 국내{" "}
            {publicationCounts.domestic} · 저서·보고서 {publicationCounts.book}
            <br />
            공식 교수 프로필 기준
          </>
        }
      />

      <section className="page-section is-compact shell" aria-labelledby="publication-record">
        <h2 id="publication-record" className="sr-only">
          출판 기록 검색
        </h2>
        <div className="filters">
          <div className="filter-field">
            <label htmlFor="publication-search">검색</label>
            <input
              id="publication-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="논문명, 저자, 저널"
            />
          </div>
          <div className="filter-field">
            <label htmlFor="publication-type">구분</label>
            <select
              id="publication-type"
              value={type}
              onChange={(event) => setType(event.target.value as TypeFilter)}
            >
              <option value="all">전체</option>
              <option value="international">국제학술지</option>
              <option value="domestic">국내학술지</option>
              <option value="book">저서·보고서</option>
            </select>
          </div>
          <div className="filter-field">
            <label htmlFor="publication-sort">정렬</label>
            <select
              id="publication-sort"
              value={sort}
              onChange={(event) => setSort(event.target.value as SortOrder)}
            >
              <option value="newest">최신순</option>
              <option value="oldest">오래된순</option>
            </select>
          </div>
          <p className="filter-count" aria-live="polite">
            {filtered.length}건
          </p>
        </div>

        {groups.length ? (
          <div>
            {groups.map(([year, items]) => (
              <section className="publication-year-group" key={year}>
                <h2>{year}</h2>
                <div className="publication-list">
                  {items.map((publication) => (
                    <PublicationRow
                      key={publication.id}
                      publication={publication}
                      showYear={false}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <p className="empty-state">조건에 맞는 출판 기록이 없습니다.</p>
        )}
      </section>
    </>
  );
}
